import { ReoIcon, ReoInput, ReoLink } from '@reolink-web/components.basic';
import React from 'react';
import actionStyles from '../index.module.less';
import styles from './index.module.less';
import type * as I from '@/interface';
import * as Api from '@/api/client/product';
import * as utils from '@reolink-web/utils';
import classNames from 'classnames';
import { jumpToNewPage } from '@/utils/function/jumpToNewPage';
import { HeaderContext } from '@/components/ReolinkHeader/common';
import { getStorePath } from '@/utils/function/sitePath';
const CompHeaderSearch: React.FC = React.memo(() => {
    const { isShowSearch, setIsShowSearch, getTextByKey, lang, country } = React.useContext(HeaderContext);

    const [recommendedProducts, setRecommendedProducts] = React.useState<I.IProductRecommendItem[]>([]);
    const [searchedProductList, setSearchedProductList] = React.useState<I.ISearchNavProductData[]>([]);
    const [searchData, setSearchData] = React.useState<string>('');

    const searchKeyRef = React.useRef<string>('');

    const searchInputRef = React.useRef<HTMLInputElement>(null);

    const fetchRecommendedProducts = React.useCallback(async () => {
        try {
            const res = await Api.getNavProductSearchRecommend(lang);
            setRecommendedProducts(res.data);
            if (!searchKeyRef.current) {
                searchKeyRef.current = res.data?.[0]?.title;
            }
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    }, [lang]);

    const searchNavProductsRef = React.useRef(
        utils.Optimization.throttle(async (lang: string, keyword: string, country: string) => {
            try {
                const res = await Api.searchNavProductList({
                    lang,
                    keyword,
                    country,
                    limit: 8,
                });
                setSearchedProductList(res.items);
            }
            catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
            }
        }, 500),
    );

    const handleSearchRef = React.useRef(() => {
        if (!searchKeyRef.current) {
            return;
        }
        jumpToNewPage(`/sitesearch/?q=${encodeURIComponent(searchKeyRef.current)}`);
    });

    const onKeydownRef = React.useRef((e: KeyboardEvent): void => {
        if (e.key === 'Enter' || e.key === 'NumpadEnter') {
            handleSearchRef.current();
        }
    });

    const handleCloseProductSearch = React.useCallback(() => {
        setIsShowSearch?.(false);
        window.removeEventListener('keydown', onKeydownRef.current);
    }, [setIsShowSearch]);

    const handleShowProductSearch = React.useCallback(() => {
        setIsShowSearch?.(true);
        window.addEventListener('keydown', onKeydownRef.current);
    }, [setIsShowSearch]);

    const handleSearchInputChange = React.useCallback(
        (value: string) => {
            setSearchData(value);
            searchKeyRef.current = value;
            if (!value) {
                searchKeyRef.current = recommendedProducts?.[0]?.title;
            }

            if (value) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                searchNavProductsRef.current(lang, value, country);
            }
            else {
                setSearchedProductList([]);
            }
        },
        [country, lang, recommendedProducts],
    );

    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchRecommendedProducts();
    }, [fetchRecommendedProducts]);

    React.useEffect(() => {
        if (isShowSearch) {
            searchInputRef.current?.focus();
            document.body.classList.add(styles['ban-scroll']);
        }
        else {
            document.body.classList.remove(styles['ban-scroll']);
        }
    }, [isShowSearch]);

    return (
        <li
            className={ classNames(actionStyles['header-action-item'], {
                [actionStyles['header-action-item-active']]: isShowSearch,
            }) }
            onClick={ handleShowProductSearch }
        >
            <ReoIcon
                className={ actionStyles['header-action-item-icon'] }
                name={ 'icon-icon_search-2' } />
            {isShowSearch ? (
                <div
                    className={ styles['product-search'] }
                    onClick={ (e) => {
                        e.stopPropagation();
                    } }
                >
                    <div className={ styles['product-search-wrapper'] }>
                        <div className={ styles['product-search-input-wrapper'] }>
                            <ReoInput
                                className={ styles['product-search-input'] }
                                activeBorderColor="#000"
                                borderColor="#fff"
                                placeholder={ recommendedProducts?.[0]?.title }
                                value={ searchData }
                                onChange={ handleSearchInputChange }
                                allowClear={ false }
                                ref={ searchInputRef }
                            />
                            <ReoIcon
                                className={ styles['product-search-icon'] }
                                name={ 'icon-icon_search-2' }
                                onClick={ handleSearchRef.current }
                            />
                            <ReoIcon
                                className={ styles['product-search-close-icon'] }
                                name={ 'icon-cross2' }
                                onClick={ handleCloseProductSearch }
                            />
                        </div>
                        <div className={ styles['recommended-products'] }>
                            <label className={ styles['recommended-products-label'] }>
                                {getTextByKey('recommended-products')}
                            </label>
                            {searchData ? (
                                <>
                                    <ul className={ styles['searched-products-list'] }>
                                        {searchedProductList.map((item: I.ISearchNavProductData) => {
                                            return (
                                                <li
                                                    className={ styles['searched-products-item'] }
                                                    key={ item.id }>
                                                    <a
                                                        className={ styles['searched-products-item-link'] }
                                                        href={ item.href }
                                                    >
                                                        <img
                                                            className={ styles['searched-products-item-image'] }
                                                            src={ item.image.pc }
                                                            alt={ item.name }
                                                        />
                                                        <div className={ styles['searched-products-item-info'] }>
                                                            <h3
                                                                className={ styles['searched-products-item-info-title'] }
                                                                dangerouslySetInnerHTML={{
                                                                    __html: item.name.replace(
                                                                        new RegExp(searchData, 'gi'),
                                                                        (match) =>
                                                                            `<span class="${styles['searched-products-item-info-highlight']}">${match}</span>`,
                                                                    ),
                                                                }}
                                                            />
                                                            <p
                                                                className={ styles['searched-products-item-info-desc'] }
                                                                dangerouslySetInnerHTML={{
                                                                    __html: item.profile.description.replace(
                                                                        new RegExp(searchData, 'gi'),
                                                                        (match) =>
                                                                            `<span class="${styles['searched-products-item-info-highlight']}">${match}</span>`,
                                                                    ),
                                                                }}
                                                            />
                                                        </div>
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    {searchedProductList.length ? (
                                        <ReoLink
                                            href={ getStorePath() }
                                            size={ 'small' }
                                            className={ styles['search-more-products-link'] }
                                        >
                                            {getTextByKey('search-more-products')}
                                        </ReoLink>
                                    ) : null}
                                </>
                            ) : (
                                <ul className={ styles['recommended-products-list'] }>
                                    {recommendedProducts.map((item: I.IProductRecommendItem) => {
                                        return (
                                            <li
                                                className={ styles['recommended-products-item'] }
                                                key={ item.id }>
                                                <a
                                                    className={ styles['recommended-products-item-link'] }
                                                    href={ item.href }
                                                >
                                                    <img
                                                        className={ styles['recommended-products-item-image'] }
                                                        src={ item.image.pc }
                                                        alt={ item.title }
                                                    />
                                                    <p className={ styles['recommended-products-item-title'] }>
                                                        {item.title}
                                                    </p>
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}
        </li>
    );
});
export default CompHeaderSearch;
